'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function DownloadSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const el = sectionRef?.current;
    if (el) {
      el?.querySelectorAll('.reveal-up, .reveal-blur, .stagger-grid')?.forEach((child) => observer?.observe(child));
      observer?.observe(el);
    }
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="download"
      className="relative w-full py-32 px-6 lg:px-12 border-t border-white/5 overflow-hidden"
    >
      {/* Atmospheric background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(26,43,74,0.4) 0%, rgba(8,10,15,0.95) 70%)',
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(201,168,76,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Large background text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <span
          className="font-manrope font-bold text-white/[0.02] select-none"
          style={{ fontSize: '20vw', whiteSpace: 'nowrap' }}
        >
          SWOT
        </span>
      </div>
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">

          {/* Left: Book + price */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-start gap-8 reveal-blur">
            {/* Book cover */}
            <div
              className="animate-gold-pulse relative rounded-xl overflow-hidden"
              style={{
                boxShadow: '0 30px 60px rgba(0,0,0,0.7), 0 0 30px rgba(201,168,76,0.1)',
                transform: 'rotate(-1deg)',
              }}
            >
              <AppImage
                src="/assets/images/Book_Cover-1772224514588.jpg"
                alt="Mastering SWOT Analysis ebook cover - digital download"
                width={280}
                height={360}
                className="w-56 lg:w-64 object-cover"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%)' }}
              />
            </div>

            {/* Formats */}
            {/* removed PDF/ePub/Mobi buttons */}
          </div>

          {/* Right: CTA */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            <div>
              <span className="font-mono text-xs text-accent uppercase tracking-widest reveal-up block mb-4">Ready to Start?</span>
              <h2 className="font-manrope text-4xl lg:text-6xl font-semibold tracking-tight leading-[0.9] reveal-text-line">
                Turn insight
              </h2>
              <h2 className="font-manrope text-4xl lg:text-6xl font-semibold tracking-tight leading-[0.9] text-foreground/30 reveal-text-line" style={{ transitionDelay: '0.1s' }}>
                into action.
              </h2>
            </div>

            <p className="text-foreground/55 text-base leading-relaxed font-light reveal-blur max-w-md">
              Get instant access to the complete guide. Whether you&apos;re defining business
              strategy, launching a new initiative, or reassessing priorities — this guide
              will help you move forward with confidence and purpose.
            </p>

            {/* Price card */}
            <div
              className="reveal-blur rounded-2xl p-6 flex flex-col gap-5"
              style={{
                background: 'var(--surface)',
                border: '1px solid rgba(201,168,76,0.2)',
                boxShadow: '0 0 40px rgba(201,168,76,0.05)',
              }}
            >
              {/* Price */}
              <div className="flex items-end justify-between">
                <div>
                  <div className="font-mono text-xs text-foreground/35 uppercase tracking-widest mb-1">One-time purchase</div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-manrope text-5xl font-bold text-gold">$4.99</span>
                    <span className="text-foreground/35 text-sm font-mono line-through">$24.99</span>
                    <span className="text-xs font-mono text-accent bg-accent/10 border border-accent/20 px-2 py-0.5 rounded-full">60% off</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-xs text-foreground/30">Instant download</div>
                  <div className="font-mono text-xs text-foreground/30">No subscription</div>
                </div>
              </div>

              {/* Download button */}
              <a
                href="https://strategyshelf.mysamcart.com/checkout/mastering-swot-analysis-a-practical-guide-for-success"
                className="btn-download text-background font-manrope font-bold px-8 py-4 rounded-xl text-base flex items-center justify-center gap-3 shadow-xl w-full"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download Now — $4.99
              </a>

              <p className="text-center font-mono text-[10px] text-foreground/25 uppercase tracking-wider">
                Secure checkout · Instant access · PDF + ePub + Mobi
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}